import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import {
  Typography,
  Divider,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Paper,
  Box,
  Tooltip,
  FormControlLabel,
  Checkbox,
  FormGroup,
  FormLabel,
  Switch,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

// Estilos para ocultar spinners de inputs numéricos
const numberInputStyles = {
  "& input[type=number]": {
    MozAppearance: "textfield",
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

// IVA fijo (no editable)
const VAT_PERCENTAGE = 19;
const VAT_RATE = VAT_PERCENTAGE / 100;

// Tasa impuesto de transferencia: 1.5% del mayor valor entre Valor Mandante y Tasación Fiscal
const TRANSFER_TAX_RATE = 0.015;

// Generador de IDs únicos para costos adicionales
let costIdCounter = 0;
const generateCostId = () => {
  costIdCounter += 1;
  return `cost_${Date.now()}_${costIdCounter}`;
};

/**
 * Formatear número con separadores de miles (formato chileno, sin decimales)
 */
const formatWithThousands = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  let strValue = String(value);
  strValue = strValue.replace(/[,.]\d{0,2}$/, "");
  strValue = strValue.replace(/\./g, "");
  const parsed = parseInt(strValue, 10);
  if (Number.isNaN(parsed)) return "";
  return new Intl.NumberFormat("es-CL", { maximumFractionDigits: 0 }).format(
    parsed,
  );
};

/**
 * Input numérico optimizado (anti-lag). Mantiene estado local solo mientras
 * está enfocado para desacoplar el typing del re-render del padre.
 */
const NumericInput = React.memo(
  ({ value, onChange, formatThousands = true, ...props }) => {
    const inputRef = useRef(null);
    const isFocusedRef = useRef(false);
    const cursorPosRef = useRef(null);
    const [localDisplay, setLocalDisplay] = useState(null);

    const rawValue = value !== undefined && value !== null ? String(value) : "";
    const fromProp = formatThousands ? formatWithThousands(rawValue) : rawValue;
    const displayValue = localDisplay !== null ? localDisplay : fromProp;

    useEffect(() => {
      if (
        isFocusedRef.current &&
        cursorPosRef.current !== null &&
        inputRef.current
      ) {
        const pos = cursorPosRef.current;
        cursorPosRef.current = null;
        try {
          inputRef.current.setSelectionRange(pos, pos);
        } catch {
          /* algunos tipos no soportan setSelectionRange */
        }
      }
    }, [displayValue]);

    const handleChange = useCallback(
      (e) => {
        const input = e.target;
        const cursorPos = input.selectionStart ?? input.value.length;
        const newValue = input.value;

        if (formatThousands) {
          const digitsOnly = newValue.replace(/[^0-9]/g, "");
          const formatted = digitsOnly ? formatWithThousands(digitsOnly) : "";
          const digitsBeforeCursor = newValue
            .slice(0, cursorPos)
            .replace(/[^0-9]/g, "").length;
          let newCursorPos = 0;
          let digitCount = 0;
          for (
            let i = 0;
            i < formatted.length && digitCount < digitsBeforeCursor;
            i += 1
          ) {
            if (formatted[i] !== ".") {
              digitCount += 1;
            }
            newCursorPos = i + 1;
          }
          if (digitsBeforeCursor === 0) newCursorPos = 0;
          cursorPosRef.current = newCursorPos;
          setLocalDisplay(formatted);
          onChange({ target: { value: digitsOnly } });
        } else {
          cursorPosRef.current = cursorPos;
          setLocalDisplay(newValue);
          onChange({ target: { value: newValue } });
        }
      },
      [onChange, formatThousands],
    );

    const handleFocus = useCallback(
      (e) => {
        isFocusedRef.current = true;
        setLocalDisplay(
          formatThousands ? formatWithThousands(rawValue) : rawValue,
        );
        if (props.onFocus) props.onFocus(e);
      },
      [formatThousands, rawValue, props],
    );

    const handleBlur = useCallback(
      (e) => {
        isFocusedRef.current = false;
        setLocalDisplay(null);
        if (props.onBlur) props.onBlur(e);
      },
      [props],
    );

    const handleWheel = useCallback((e) => {
      e.target.blur();
      e.preventDefault();
    }, []);

    return (
      <TextField
        {...props}
        inputRef={inputRef}
        type={formatThousands ? "text" : props.type}
        value={displayValue}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onWheel={handleWheel}
        inputProps={{
          ...props.inputProps,
          inputMode: formatThousands ? "numeric" : undefined,
        }}
        sx={{ ...numberInputStyles, ...props.sx }}
      />
    );
  },
);
NumericInput.displayName = "NumericInput";

/**
 * TextField con buffer local mientras está enfocado (anti-lag para nombre/desc).
 */
const FocusBufferedTextField = React.memo(({ value, onChange, ...props }) => {
  const [localValue, setLocalValue] = useState(null);
  const display = localValue !== null ? localValue : (value ?? "");

  const handleChange = useCallback(
    (e) => {
      setLocalValue(e.target.value);
      onChange(e);
    },
    [onChange],
  );

  const handleFocus = useCallback(
    (e) => {
      setLocalValue(value ?? "");
      if (props.onFocus) props.onFocus(e);
    },
    [value, props],
  );

  const handleBlur = useCallback(
    (e) => {
      setLocalValue(null);
      if (props.onBlur) props.onBlur(e);
    },
    [props],
  );

  return (
    <TextField
      {...props}
      value={display}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});
FocusBufferedTextField.displayName = "FocusBufferedTextField";

const AdditionalCostItem = React.memo(({ cost, onUpdate, onRemove }) => {
  const handleNameChange = useCallback(
    (e) => onUpdate("name", e.target.value),
    [onUpdate],
  );
  const handleValueChange = useCallback(
    (e) => onUpdate("value", e.target.value),
    [onUpdate],
  );
  const handleDescriptionChange = useCallback(
    (e) => onUpdate("description", e.target.value),
    [onUpdate],
  );

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1.5,
        p: { xs: 1.5, sm: 2 },
        mt: 1.5,
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" fontWeight="bold">
          {cost.name?.trim() ? cost.name : "Nuevo costo"}
        </Typography>
        <IconButton
          color="error"
          onClick={onRemove}
          size="small"
          aria-label="eliminar costo"
        >
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "5fr 4fr" },
          gap: 1.5,
        }}
      >
        <FocusBufferedTextField
          fullWidth
          size="small"
          label="Nombre"
          placeholder="Ej: Transporte"
          value={cost.name ?? ""}
          onChange={handleNameChange}
        />
        <NumericInput
          fullWidth
          size="small"
          label="Valor"
          value={cost.value ?? ""}
          onChange={handleValueChange}
          formatThousands
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
        />
        <FocusBufferedTextField
          fullWidth
          size="small"
          label="Descripción (opcional)"
          value={cost.description ?? ""}
          onChange={handleDescriptionChange}
          sx={{ gridColumn: { sm: "1 / -1" } }}
        />
      </Box>
    </Paper>
  );
});
AdditionalCostItem.displayName = "AdditionalCostItem";

/**
 * Formulario de detalles de precio de venta directa (versión venta-usados).
 *
 * Componente controlado: el padre (modal) mantiene `data` y `onChange` aplica
 * patches parciales. Solo gestiona los campos de ENTRADA; los valores derivados
 * (netPrice, totalPrice, transferTax, etc.) los recalcula el backend al guardar.
 * Aquí se muestra una previsualización en vivo idéntica a la fórmula del backend.
 *
 * @param {Object} props
 * @param {Object} props.data - Inputs del detalle de precio
 * @param {(patch: Object) => void} props.onChange - Aplica un patch parcial a data
 */
function PriceDetailsForm({ data = {}, onChange }) {
  // 🆕 Detalle de precio activo. Si false, el producto usa el precio + IVA
  //     legacy del lote (no se publica el desglose). Default: activo (true)
  const active = data.active !== false;
  // 🆕 Afecto/exento a IVA (booleano). Default: afecto (true)
  const iva = data.iva !== false;
  // 🆕 Tiene patente (para impuesto transferencia). Default: true (con patente)
  const hasLicensePlate = data.hasLicensePlate !== false;

  // Valor Mandante (lo que recibe el dueño del bien)
  const ownerPrice = parseFloat(data.ownerPrice) || 0;

  const ownerPriceVat = useMemo(() => {
    if (!iva) return 0;
    return Math.round(ownerPrice * VAT_RATE);
  }, [ownerPrice, iva]);

  // Valores de entrada del usuario
  const commissionPercentage = parseFloat(data.commissionPercentage) || 0;
  const operationalCosts = parseFloat(data.operationalCosts) || 0;
  const operationalCostsVat = useMemo(() => {
    return Math.round(operationalCosts * VAT_RATE);
  }, [operationalCosts]);
  const transferFee = useMemo(() => {
    if (!hasLicensePlate) return 0;
    return parseFloat(data.transferFee) || 0;
  }, [hasLicensePlate, data.transferFee]);
  // 🆕 Tasación Fiscal (valor manual obtenido del SII)
  const taxAppraisal = parseFloat(data.taxAppraisal) || 0;

  // 🆕 Comisión = Valor Mandante Neto × % (sobre el neto, sin sumar IVA)
  const commissionAmount = useMemo(
    () => Math.round((ownerPrice * commissionPercentage) / 100),
    [ownerPrice, commissionPercentage],
  );

  // 🆕 IVA de la comisión: SIEMPRE se calcula (aunque el producto sea exento)
  const commissionVat = useMemo(
    () => Math.round(commissionAmount * VAT_RATE),
    [commissionAmount],
  );

  // 🆕 Valor Neto = Valor Mandante + Comisión + IVA Comisión + Gastos Operacionales
  // Añadir al calculo los ivas de el owner de los gastos operacionales y de la comisión
  // Se debe crear un campo nuevo donde se le haga el neto + iva
  const netPrice = useMemo(
    () => Math.round(ownerPrice + commissionAmount + operationalCosts),
    [ownerPrice, commissionAmount, operationalCosts],
  );

  // 🆕 Valor Neto + IVAs = Neto + IVA Mandante + IVA Comisión + IVA Gastos Op.
  const netPriceWithVat = useMemo(
    () => netPrice + operationalCostsVat + commissionVat + ownerPriceVat,
    [netPrice, operationalCostsVat, commissionVat, ownerPriceVat],
  );

  // 🆕 Impuesto de transferencia: 1.5% del mayor valor entre Valor Mandante
  //     y Tasación Fiscal; 0 si no tiene patente
  const transferTax = useMemo(() => {
    if (!hasLicensePlate) return 0;
    const baseValue = Math.max(ownerPrice, taxAppraisal);
    return Math.round(baseValue * TRANSFER_TAX_RATE);
  }, [hasLicensePlate, ownerPrice, taxAppraisal]);

  // Costos adicionales totales
  const totalAdditionalCosts = useMemo(() => {
    const additionalCosts = data.additionalCosts || [];
    return additionalCosts.reduce(
      (sum, e) => sum + (parseFloat(e.value) || 0),
      0,
    );
  }, [data.additionalCosts]);

  // 🆕 TOTAL A PAGAR = Valor Neto + IVA Neto + IVA Comisión + Impuesto Transf
  //                    + Costo Transf + Adicionales
  const totalPrice = useMemo(
    () =>
      Math.round(
        netPriceWithVat + transferTax + transferFee + totalAdditionalCosts,
      ),
    [netPriceWithVat, transferTax, transferFee, totalAdditionalCosts],
  );

  // Manejador de cambio de campo
  const handleFieldChange = useCallback(
    (field) =>
      ({ target: { value } }) => {
        onChange({ [field]: value });
      },
    [onChange],
  );

  const handleIvaChange = useCallback(
    (value) => () => onChange({ iva: value }),
    [onChange],
  );

  const handleActiveChange = useCallback(
    (e) => onChange({ active: e.target.checked }),
    [onChange],
  );

  const handleLicensePlateChange = useCallback(
    (value) => () => onChange({ hasLicensePlate: value }),
    [onChange],
  );

  const handleAddAdditionalCost = useCallback(() => {
    const additionalCosts = data.additionalCosts || [];
    onChange({
      additionalCosts: [
        ...additionalCosts,
        { id: generateCostId(), name: "", value: "", description: "" },
      ],
    });
  }, [onChange, data.additionalCosts]);

  const handleRemoveAdditionalCost = useCallback(
    (costId, index) => () => {
      const additionalCosts = (data.additionalCosts || []).filter(
        (cost, idx) => (costId ? cost.id !== costId : idx !== index),
      );
      onChange({ additionalCosts });
    },
    [onChange, data.additionalCosts],
  );

  const handleAdditionalCostUpdate = useCallback(
    (costId, index) => (field, value) => {
      const additionalCosts = (data.additionalCosts || []).map((cost, idx) => {
        const isMatch = costId ? cost.id === costId : idx === index;
        return isMatch ? { ...cost, [field]: value } : cost;
      });
      onChange({ additionalCosts });
    },
    [onChange, data.additionalCosts],
  );

  const formatNumber = useCallback((num) => {
    if (!num && num !== 0) return "";
    return new Intl.NumberFormat("es-CL").format(num);
  }, []);

  return (
    <Paper
      elevation={0}
      sx={{ p: { xs: 1.5, sm: 3 }, backgroundColor: "#f8f9fa" }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1}>
        <Typography
          color="primary"
          variant="h5"
          sx={{ wordBreak: "break-word", minWidth: 0 }}
        >
          💰 Detalles de Precio - Venta Directa
        </Typography>
        <Tooltip title="Configure los valores para calcular el precio final que verá el cliente">
          <InfoOutlinedIcon color="action" fontSize="small" />
        </Tooltip>
      </Box>
      <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
        El valor a publicar será el precio final que pagará el cliente (sin
        sorpresas).
      </Typography>
      <Divider sx={{ mt: 1, mb: 2 }} />

      {/* Activar / desactivar detalle de precio */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 2,
          borderRadius: 1,
          border: active ? "1px solid #66bb6a" : "1px solid #ef9a9a",
          backgroundColor: active ? "#e8f5e9" : "#ffebee",
        }}
      >
        <FormControlLabel
          control={
            <Switch
              checked={active}
              onChange={handleActiveChange}
              color="success"
            />
          }
          label={
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                {active
                  ? "✅ Detalle de precio ACTIVO"
                  : "⛔ Detalle de precio DESACTIVADO"}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {active
                  ? "El producto se publica con este desglose (precio e IVA calculados aquí)."
                  : 'El producto usará el precio y el IVA "antiguo" del lote. Este desglose no se publica.'}
              </Typography>
            </Box>
          }
          sx={{ alignItems: "flex-start", m: 0 }}
        />
      </Paper>

      {/* Configuración de cálculo */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          backgroundColor: "#fff3e0",
          border: "1px solid #ffb74d",
          borderRadius: 1,
          overflow: "hidden",
          mb: 2,
        }}
      >
        <Box display="flex" alignItems="center" gap={1} mb={1.5}>
          <Typography
            variant="subtitle2"
            color="text.primary"
            fontWeight="bold"
          >
            ⚙️ Configuración de Cálculo
          </Typography>
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
            gap: 1.5,
          }}
        >
          {/* IVA: Afecto / Exento */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: "#fafafa",
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              minWidth: 0,
            }}
          >
            <FormLabel
              component="legend"
              sx={{ mb: 0.5, fontWeight: "bold", fontSize: "0.8rem" }}
            >
              IVA
            </FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={iva === true}
                    onChange={handleIvaChange(true)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="caption">💵 Afecto</Typography>}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={iva === false}
                    onChange={handleIvaChange(false)}
                    color="primary"
                    size="small"
                  />
                }
                label={<Typography variant="caption">🚫 Exento</Typography>}
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
            </FormGroup>
          </Paper>

          {/* Tiene Patente */}
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              backgroundColor: "#fafafa",
              border: hasLicensePlate
                ? "1px solid #e0e0e0"
                : "2px solid #e0e0e0",
              borderRadius: 1,
              minWidth: 0,
              transition: "all 0.2s ease",
            }}
          >
            <FormLabel
              component="legend"
              sx={{
                mb: 0.5,
                fontWeight: "bold",
                fontSize: "0.8rem",
                display: "flex",
                alignItems: "center",
                flexWrap: "nowrap",
              }}
            >
              Patente
              <Tooltip title="Sin patente = sin impuesto de transferencia">
                <InfoOutlinedIcon sx={{ ml: 0.5, fontSize: 14 }} />
              </Tooltip>
            </FormLabel>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasLicensePlate === true}
                    onChange={handleLicensePlateChange(true)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">🚗 Con Patente</Typography>
                }
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasLicensePlate === false}
                    onChange={handleLicensePlateChange(false)}
                    color="warning"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">🚜 Sin Patente</Typography>
                }
                sx={{
                  m: 0,
                  "& .MuiFormControlLabel-label": { fontSize: "0.75rem" },
                }}
              />
            </FormGroup>
          </Paper>
        </Box>
      </Paper>

      <Divider sx={{ my: 1 }} />
      <Typography variant="subtitle1" color="primary" sx={{ mb: 2 }}>
        📝 Valores de Entrada
      </Typography>

      {/* Inputs principales */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <NumericInput
            fullWidth
            label="Valor Mandante"
            value={data.ownerPrice ?? ""}
            onChange={handleFieldChange("ownerPrice")}
            formatThousands
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Monto que recibe el dueño del bien"
          />
          <TextField
            fullWidth
            label="IVA Mandante ($)"
            value={formatNumber(ownerPriceVat)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Calculado automáticamente"
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#1976d2",
                WebkitTextFillColor: "#1976d2",
              },
            }}
          />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <NumericInput
            fullWidth
            label="Comisión Tattersall"
            type="number"
            value={data.commissionPercentage ?? ""}
            onChange={handleFieldChange("commissionPercentage")}
            formatThousands={false}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            helperText="Porcentaje de comisión"
          />
          <TextField
            fullWidth
            label="Comisión ($)"
            value={formatNumber(commissionAmount)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Calculado automáticamente"
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#1976d2",
                WebkitTextFillColor: "#1976d2",
              },
            }}
          />
        </Box>

        <TextField
          fullWidth
          label="IVA Comisión ($)"
          value={formatNumber(commissionVat)}
          disabled
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          helperText="19% de la comisión (siempre se calcula, aunque sea exento)"
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              color: "#1976d2",
              WebkitTextFillColor: "#1976d2",
            },
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <NumericInput
            fullWidth
            label="Gastos Operacionales"
            value={data.operationalCosts ?? ""}
            onChange={handleFieldChange("operationalCosts")}
            formatThousands
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Gastos operacionales (IVA incluido)"
          />
          <TextField
            fullWidth
            label="IVA Gastos Operacionales ($)"
            value={formatNumber(operationalCostsVat)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Calculado automáticamente"
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#1976d2",
                WebkitTextFillColor: "#1976d2",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            label="Valor Neto (Calculado)"
            value={formatNumber(netPrice)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Mandante + Comisión + Gastos Operacionales"
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#2e7d32",
                WebkitTextFillColor: "#2e7d32",
                fontWeight: "bold",
              },
            }}
          />
          <TextField
            fullWidth
            label="Total (Valor Neto + IVAs)"
            value={formatNumber(netPriceWithVat)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Valor Neto + IVA Mandante + IVA Comisión + IVA Gastos Operacionales"
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#1976d2",
                WebkitTextFillColor: "#1976d2",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <NumericInput
            fullWidth
            label="Tasación Fiscal"
            value={data.taxAppraisal ?? ""}
            onChange={handleFieldChange("taxAppraisal")}
            formatThousands
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText="Valor de tasación fiscal del bien (del SII)"
          />

          <TextField
            fullWidth
            label="Impuesto de Transferencia"
            value={formatNumber(transferTax)}
            disabled
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            helperText={
              hasLicensePlate
                ? "1.5% del mayor valor entre Valor Mandante y Tasación Fiscal"
                : "Sin patente: no aplica impuesto de transferencia"
            }
            sx={{
              "& .MuiInputBase-input.Mui-disabled": {
                color: "#1976d2",
                WebkitTextFillColor: "#1976d2",
              },
            }}
          />
        </Box>
        <NumericInput
          fullWidth
          label="Costo Transferencia (Fijo)"
          value={transferFee}
          onChange={handleFieldChange("transferFee")}
          formatThousands
          disabled={data.hasLicensePlate === false}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          helperText="Costo fijo de transferencia"
        />
      </Box>

      {/* Costos Adicionales */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 3 }}
      >
        <Typography variant="subtitle2" color="textSecondary">
          Costos Adicionales
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AddCircleOutlineIcon />}
          onClick={handleAddAdditionalCost}
        >
          Agregar
        </Button>
      </Box>

      {(data.additionalCosts || []).map((cost, index) => (
        <AdditionalCostItem
          key={cost.id || `legacy-${index}`}
          cost={cost}
          onUpdate={handleAdditionalCostUpdate(cost.id, index)}
          onRemove={handleRemoveAdditionalCost(cost.id, index)}
        />
      ))}

      {/* Desglose Final */}
      <Divider sx={{ my: 2 }} />
      <Paper
        elevation={0}
        sx={{
          p: 2,
          backgroundColor: "#f5f5f5",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          color="primary"
          fontWeight="bold"
          sx={{ mb: 2 }}
        >
          📊 Desglose Final
        </Typography>

        {[
          { label: "Valor Neto:", value: netPrice },
          {
            label: "IVA (19%):",
            value: ownerPriceVat + commissionVat + operationalCostsVat,
          },
          {
            label: "Impuesto Transferencia:",
            value: transferTax,
            note: !hasLicensePlate ? "(Sin patente)" : null,
          },
          { label: "Costo Transferencia:", value: transferFee },
          ...(totalAdditionalCosts > 0
            ? [{ label: "Costos Adicionales:", value: totalAdditionalCosts }]
            : []),
        ].map((row) => (
          <Box
            key={row.label}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              py: 0.25,
            }}
          >
            <Typography variant="body2">{row.label}</Typography>
            <Typography variant="body2" fontWeight="medium">
              ${formatNumber(row.value)}
              {row.note && (
                <Typography
                  component="span"
                  variant="caption"
                  color="textSecondary"
                  sx={{ ml: 1 }}
                >
                  {row.note}
                </Typography>
              )}
            </Typography>
          </Box>
        ))}
      </Paper>

      {/* Valor a Publicar y Total a Pagar */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mt: 2,
        }}
      >
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            backgroundColor: "#e3f2fd",
            border: "3px solid #1976d2",
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" color="primary.dark" fontWeight="bold">
            💰 Total a Pagar
          </Typography>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ display: "block", mb: 1 }}
          >
            Precio final con todos los costos incluidos
          </Typography>
          <Typography variant="h4" color="primary.dark" fontWeight="bold">
            ${formatNumber(totalPrice)}
          </Typography>
        </Paper>
      </Box>
    </Paper>
  );
}

export default React.memo(PriceDetailsForm);
